import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "./Questions.scss";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SubHeader from "../../components/SubHeader/SubHeader";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomModal from "../../components/CustomModal/CustomModal";
import InputField from "../../components/InputField/InputField";
import DateField from "../../components/DateField/DateField";
import TextAreaField from "../../components/TextAreaField/TextAreaField";
import SelectInput from "../../components/SelectInput/SelectInput";
import ImageUploadField from "../../components/ImageUploadField/ImageUploadField";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import Loader from "../../components/Loader/Loader";
import { EditTwoTone, DeleteTwoTone, InfoCircleOutlined } from "@ant-design/icons";
import { errorToast, successToast } from "../../services/ToastHelper";
import {
    ADD_QUESTION,
    DELETE_QUESTION,
    GET_QUESTIONS_BY_MODULE,
    UPDATE_QUESTION,
    GET_SYLLABUS,
} from "../../utils/apiPath";
import { deleteApi, postApi, getApi } from "../../utils/apiService";
import { Button, Checkbox, Tooltip, Popover } from "antd";
import { getPermissionForModule } from "../../services/permissions";

const DEFAULT_OPTION_SETS = [
    {
        label: "Option Set 1",
        options: ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"],
    },
    {
        label: "Option Set 2",
        options: ["Loved It", "Sometime", "Not Really"],
    },
];

const DefaultOptionsPopoverContent = (
    <div style={{ minWidth: 200 }}>
        {DEFAULT_OPTION_SETS.map((set, si) => (
            <div key={si} style={{ marginBottom: si < DEFAULT_OPTION_SETS.length - 1 ? 12 : 0 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>{set.label}</strong>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                    {set.options.map((opt, oi) => (
                        <li key={oi} style={{ fontSize: 12, lineHeight: '20px' }}>{opt}</li>
                    ))}
                </ol>
            </div>
        ))}
    </div>
);

const QUESTION_TYPES = [
    { label: "Short Text", value: 1 },
    { label: "Long Text", value: 2 },
    { label: "Single Choice", value: 3 },
    { label: "Multi Choice", value: 4 },
    { label: "Date", value: 5 },
    { label: "Paragraph", value: 6 },
];

const Questions = () => {
    const { id, gradeId, moduleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { assessmentTypeName, subject, gradeName, moduleName } = location.state || {}; // Retrieve state
    const assessmentLabel = subject ? `${assessmentTypeName} (${subject})` : assessmentTypeName;
    const currentSubTitle = [assessmentLabel, gradeName, moduleName].filter(Boolean).join(" > "); // Construct breadcrumb

    const [isLoading, setIsLoading] = useState(false);
    const [questionsData, setQuestionsData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [optionErrors, setOptionErrors] = useState([]);
    const [mode, setMode] = useState("add"); // "add" | "edit"
    const [editingId, setEditingId] = useState(null);
    const [syllabusOptions, setSyllabusOptions] = useState([]);

    const questionsPermission = getPermissionForModule("Assessment Questions");

    // Form State
    const [formData, setFormData] = useState({
        questionText: "",
        questionMediaUrl: "",
        questionType: 1,
        answerKeyText: "",
        marks: 1,
        sortOrder: 1,
        isDefaultOptions: false,
        defaultOptionSet: 0,
        childQuestions: [],
        options: [
            { optionText: "", isCorrect: false },
            { optionText: "", isCorrect: false },
        ],
        syllabusId: null,
    });

    const fetchQuestions = async () => {
        setIsLoading(true);
        try {
            const payload = {
                gradeId,
                moduleIds: [moduleId],
                id,
            };
            const { statusCode, data, message } = await postApi(
                GET_QUESTIONS_BY_MODULE,
                payload
            );
            if (statusCode === 200) {
                const modules = data?.modules || [];
                // Flatten questions from all returned modules (should be just one based on our request)
                const allQuestions = modules.flatMap((m) => m.questions || []);
                setQuestionsData(allQuestions);
            } else {
                errorToast(message);
            }
        } catch (err) {
            console.error(err);
            errorToast("Failed to fetch questions");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSyllabusOptions = async () => {
        try {
            const payload = {
                assessmentTypeId: id,
                gradeId: gradeId,
            };
            const { statusCode, data } = await postApi(GET_SYLLABUS, payload);
            if (statusCode === 200) {
                const options = (data || []).map(s => ({
                    label: s.name,
                    value: s.id
                }));
                setSyllabusOptions(options);
            }
        } catch (err) {
            console.error("Failed to fetch syllabuses", err);
        }
    };

    useEffect(() => {
        if (moduleId && gradeId && id) {
            fetchQuestions();
            fetchSyllabusOptions();
        }
    }, [moduleId, gradeId, id]);

    const handleAddQuestion = () => {
        setMode("add");
        setEditingId(null);
        setFormData({
            questionText: "",
            questionMediaUrl: "",
            questionType: 1,
            answerKeyText: "", // For Text questions
            marks: 1,
            sortOrder: (questionsData.length || 0) + 1,
            isDefaultOptions: false,
            defaultOptionSet: 0,
            childQuestions: [],
            options: [
                { optionText: "", isCorrect: false },
                { optionText: "", isCorrect: false },
                { optionText: "", isCorrect: false },
                { optionText: "", isCorrect: false },
            ],
            syllabusId: null,
        });
        setOptionErrors([]);
        setIsModalOpen(true);
    };

    const handleEdit = (record) => {
        setMode("edit");
        setEditingId(record.questionId || record.id); // Adjust based on API response key
        setFormData({
            questionText: record.questionText,
            questionMediaUrl: record.questionMediaUrl || "",
            questionType: record.questionType,
            answerKeyText: record.answerKeyText || "",
            marks: record.marks || 0,
            sortOrder: record.sortOrder || 1,
            isDefaultOptions: record.isDefaultOptions || false,
            defaultOptionSet: record.defaultOptionSet || 0,
            childQuestions: (record.childQuestions || []).map(cq => ({
                ...cq,
                syllabusId: cq.syllabusId || cq.SyllabusId || null
            })),
            options:
                record.options && record.options.length > 0
                    ? record.options.map(opt => ({
                        ...opt,
                        isCorrect: opt.isCorrect,
                    }))
                    : [
                        { optionText: "", isCorrect: false },
                        { optionText: "", isCorrect: false },
                    ],
            syllabusId: record.syllabusId || record.SyllabusId || null,
        });
        setOptionErrors([]);
        setIsModalOpen(true);
    };

    const handleDelete = async (record) => {
        setIsLoading(true);
        try {
            const { statusCode, message } = await deleteApi(DELETE_QUESTION, {
                params: { id: record.questionId || record.id },
            });
            if (statusCode === 200) {
                successToast(message);
                await fetchQuestions();
            } else {
                errorToast(message);
            }
        } catch (error) {
            errorToast("Failed to delete question");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => setIsModalOpen(false);

    // Form Handlers
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = { ...newOptions[index], [field]: value };

        // If Single Choice (3), ensure only one isCorrect is true
        if (field === "isCorrect" && value === true && formData.questionType === 3) {
            newOptions.forEach((opt, i) => {
                if (i !== index) opt.isCorrect = false;
            });
        }
        setFormData((prev) => ({ ...prev, options: newOptions }));
    };

    const handleInputChangeForOption = (index, field, value) => {
        handleOptionChange(index, field, value);
    };

    const addOption = () => {
        setFormData((prev) => ({
            ...prev,
            options: [...prev.options, { optionText: "", isCorrect: false }],
        }));
    };

    const removeOption = (index) => {
        setFormData((prev) => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index),
        }));
    };

    // Child Question Handlers
    const handleAddChildQuestion = () => {
        setFormData((prev) => ({
            ...prev,
            childQuestions: [
                ...prev.childQuestions,
                {
                    questionId: null, // New child question
                    questionText: "",
                    questionMediaUrl: "",
                    questionType: 1,
                    answerKeyText: "",
                    marks: 1,
                    sortOrder: prev.childQuestions.length + 1,
                    isDefaultOptions: false,
                    options: [
                        { optionText: "", isCorrect: false },
                        { optionText: "", isCorrect: false },
                    ],
                    syllabusId: null,
                }
            ]
        }));
    };

    const handleRemoveChildQuestion = (index) => {
        setFormData((prev) => ({
            ...prev,
            childQuestions: prev.childQuestions.filter((_, i) => i !== index),
        }));
    };

    const handleChildQuestionChange = (index, field, value) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[index] = { ...newChildQuestions[index], [field]: value };
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    // Child Question Options Handlers
    const handleChildOptionChange = (childIndex, optionIndex, field, value) => {
        const newChildQuestions = [...formData.childQuestions];
        const childQuestion = newChildQuestions[childIndex];
        const newOptions = [...childQuestion.options];
        newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };

        if (field === "isCorrect" && value === true && childQuestion.questionType === 3) {
            newOptions.forEach((opt, i) => {
                if (i !== optionIndex) opt.isCorrect = false;
            });
        }

        childQuestion.options = newOptions;
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleAddChildOption = (childIndex) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[childIndex].options.push({ optionText: "", isCorrect: false });
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleRemoveChildOption = (childIndex, optionIndex) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[childIndex].options = newChildQuestions[childIndex].options.filter((_, i) => i !== optionIndex);
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.questionText.trim()) {
            errorToast("Question Text is required");
            return;
        }
        if ((formData.questionType === 3 || formData.questionType === 4) && !formData.isDefaultOptions) {
            const newOptionErrors = formData.options.map((o) =>
                o.optionText.trim() ? "" : "Option is required"
            );
            if (newOptionErrors.some((e) => e)) {
                setOptionErrors(newOptionErrors);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                moduleId,
                syllabusId: formData.syllabusId,
                questionText: formData.questionText,
                questionMediaUrl: formData.questionMediaUrl,
                questionType: formData.questionType,
                answerKeyText: formData.answerKeyText,
                answerKeyDate: formData.questionType === 5 && formData.answerKeyDate ? dayjs(formData.answerKeyDate).toISOString() : null,
                marks: 0,
                sortOrder: Number(formData.sortOrder),
                isDefaultOptions: formData.isDefaultOptions,
                defaultOptionSet: formData.isDefaultOptions ? formData.defaultOptionSet : 0,
                childQuestions: formData.questionType === 6
                    ? formData.childQuestions.map((child, idx) => ({
                        moduleId, // Inherit module ID
                        questionText: child.questionText,
                        questionMediaUrl: child.questionMediaUrl || "",
                        questionType: child.questionType,
                        answerKeyText: child.answerKeyText || "",
                        answerKeyDate: child.questionType === 5 && child.answerKeyDate ? dayjs(child.answerKeyDate).toISOString() : null,
                        marks: child.marks || 0,
                        sortOrder: Number(child.sortOrder) || (idx + 1),
                        isDefaultOptions: child.isDefaultOptions,
                        defaultOptionSet: child.isDefaultOptions ? (child.defaultOptionSet || 0) : 0,
                        options: (child.questionType === 3 || child.questionType === 4) && !child.isDefaultOptions
                            ? child.options.map((opt, oIdx) => ({
                                optionText: opt.optionText,
                                optionMediaUrl: opt.optionMediaUrl || "",
                                isCorrect: opt.isCorrect,
                                sortOrder: oIdx + 1,
                                ...(mode === "edit" && opt.optionId ? { optionId: opt.optionId } : {})
                            }))
                            : [],
                        // Maintain ID if editing an existing child question
                        ...(child.questionId ? { questionId: child.questionId } : {}),
                        syllabusId: child.syllabusId
                    }))
                    : (mode === "edit" ? formData.childQuestions : []), // Preserve existing childQuestions if not type 6, or just empty

                options: (formData.questionType === 3 || formData.questionType === 4) && !formData.isDefaultOptions
                    ? formData.options.map((opt, idx) => ({
                        optionText: opt.optionText,
                        optionMediaUrl: opt.optionMediaUrl || "",
                        isCorrect: opt.isCorrect,
                        sortOrder: idx + 1,
                        // If editing, might need optionId if updating existing options, 
                        // but usually APIs handle replacement or update by ID.
                        // The user didn't specify option update logic deeply, assuming full replace or API handles it.
                        ...(mode === "edit" && opt.optionId ? { optionId: opt.optionId } : {})
                    }))
                    : []
            };

            let result;
            if (mode === "add") {
                result = await postApi(ADD_QUESTION, payload);
            } else {
                result = await postApi(UPDATE_QUESTION, {
                    ...payload,
                    questionId: editingId
                });
            }

            if (result.statusCode === 200) {
                successToast(result.message);
                setIsModalOpen(false);
                fetchQuestions();
            } else {
                errorToast(result.message);
            }

        } catch (err) {
            console.error(err);
            errorToast("Operation failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                title: "Sl No",
                dataIndex: "sortOrder",
                key: "sortOrder",
                width: 80,
            },
            {
                title: "Question",
                dataIndex: "questionText",
                key: "questionText",
                render: (text) => (
                    <div style={{ maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={text}>
                        {text}
                    </div>
                )
            },
            {
                title: "Type",
                dataIndex: "questionType",
                key: "questionType",
                render: (val) => QUESTION_TYPES.find(t => t.value === val)?.label || val,
            },
            {
                title: "Action",
                key: "action",
                width: 120,
                render: (_, record) => (
                    <ActionGroup>
                        {questionsPermission?.canEdit && (
                            <ActionIcon
                                title="Edit"
                                icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                                onClick={() => handleEdit(record)}
                            />
                        )}
                        {questionsPermission?.canDelete && (
                            <ActionIcon
                                title="Delete"
                                icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                confirm={{
                                    enabled: true,
                                    title: "Delete this question?",
                                    onConfirm: () => handleDelete(record),
                                }}
                            />
                        )}
                    </ActionGroup>
                ),
            },
        ],
        [questionsData]
    );

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className="questions-page">
            {isLoading && <Loader />}
            <SubHeader
                title="Questions"
                subTitle={currentSubTitle}
                showBack={true}
                showRight={questionsPermission?.canAdd}
                rightActionLabel={questionsPermission?.canAdd ? "Add Question" : undefined}
                onRightClick={questionsPermission?.canAdd ? handleAddQuestion : undefined}
                onBack={handleBack}
            />

            <CustomTable
                columns={columns}
                dataSource={questionsData}
                loading={isLoading}
                rowKey={(record) => record.questionId || record.id}
                pageIndex={pageIndex}
                pageSize={pageSize}
                total={questionsData.length}
                onPageChange={(current, size) => {
                    const newPageIndex = size !== pageSize ? 0 : current - 1;
                    setPageIndex(newPageIndex);
                    setPageSize(size);
                }}
            />

            <CustomModal
                open={isModalOpen}
                title={mode === "edit" ? "Edit Question" : "Add Question"}
                onClose={handleCloseModal}
                primaryText={mode === "edit" ? "Update" : "Add"}
                onPrimary={handleSubmit}
                primaryProps={{ loading: isSubmitting }}
                width={800}
            >
                <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                        <SelectInput
                            title="Question Type"
                            options={QUESTION_TYPES}
                            value={formData.questionType}
                            onChange={(val) => handleInputChange("questionType", val)}
                            required
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div className="question-marks-row">
                            <InputField
                                title="Sort Order"
                                type="number"
                                value={formData.sortOrder}
                                onChange={(e) => handleInputChange("sortOrder", e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <SelectInput
                        title="Syllabus"
                        options={syllabusOptions}
                        value={formData.syllabusId}
                        onChange={(val) => handleInputChange("syllabusId", val)}
                        placeholder="Select syllabus (Optional)"
                        required
                    />
                </div>

                <TextAreaField
                    title="Question Text"
                    value={formData.questionText}
                    onChange={(e) => handleInputChange("questionText", e.target.value)}
                    required
                    rows={3}
                />

                <div style={{ marginTop: 16 }}>
                    <span className="input-label" style={{ display: 'block', marginBottom: 8 }}>Question Image (Optional)</span>
                    <ImageUploadField
                        value={formData.questionMediaUrl}
                        onChange={(_, url) => handleInputChange("questionMediaUrl", url)}
                    />
                </div>

                {/* Dynamic Fields based on Type */}
                {formData.questionType === 1 && (
                    <InputField
                        title="Answer Key / Model Answer"
                        value={formData.answerKeyText}
                        onChange={(e) => handleInputChange("answerKeyText", e.target.value)}
                        placeholder="Enter correct answer"
                        required
                    />
                )}

                {formData.questionType === 2 && (
                    <TextAreaField
                        title="Answer Key / Model Answer"
                        value={formData.answerKeyText}
                        onChange={(e) => handleInputChange("answerKeyText", e.target.value)}
                        placeholder="Enter correctness criteria"
                        rows={3}
                    />
                )}

                {formData.questionType === 5 && (
                    <DateField
                        title="Answer Key Date"
                        value={formData.answerKeyDate ? dayjs(formData.answerKeyDate) : null}
                        onChange={(date) => handleInputChange("answerKeyDate", date)}
                        placeholder="Select correct date"
                    />
                )}

                {(formData.questionType === 3 || formData.questionType === 4) && (
                    <div className="options-container">
                        <div className="options-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span>Options ({formData.questionType === 3 ? "Single Choice" : "Multi Choice"})</span>
                                <Checkbox
                                    checked={formData.defaultOptionSet === 1}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        handleInputChange("isDefaultOptions", checked);
                                        handleInputChange("defaultOptionSet", checked ? 1 : 0);
                                    }}
                                >
                                    Default Options 1
                                </Checkbox>
                                <Checkbox
                                    checked={formData.defaultOptionSet === 2}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        handleInputChange("isDefaultOptions", checked);
                                        handleInputChange("defaultOptionSet", checked ? 2 : 0);
                                    }}
                                >
                                    Default Options 2
                                </Checkbox>
                                <Popover
                                    trigger="hover"
                                    placement="topLeft"
                                    title={<span style={{ fontWeight: 600 }}>Default Option Sets</span>}
                                    content={DefaultOptionsPopoverContent}
                                >
                                    <InfoCircleOutlined style={{ color: '#faad14', cursor: 'pointer', fontSize: 15 }} />
                                </Popover>
                            </div>
                            {!formData.isDefaultOptions && (
                                <Button type="dashed" size="small" onClick={addOption}>+ Add Option</Button>
                            )}
                        </div>

                        {!formData.isDefaultOptions && formData.options.map((opt, idx) => (
                            <div key={idx} className="option-row">
                                <div className="option-check-wrapper">
                                    <input
                                        type={formData.questionType === 3 ? "radio" : "checkbox"}
                                        className="option-check"
                                        checked={opt.isCorrect}
                                        onChange={(e) => handleOptionChange(idx, "isCorrect", e.target.checked)}
                                        name="modalOptionRadio"
                                        title="Mark as correct answer"
                                    />
                                </div>
                                <div className="option-input-wrapper">
                                    <InputField
                                        placeholder={`Option ${idx + 1}`}
                                        value={opt.optionText}
                                        onChange={(e) => {
                                            handleInputChangeForOption(idx, "optionText", e.target.value);
                                            if (optionErrors[idx]) setOptionErrors((prev) => { const updated = [...prev]; updated[idx] = ""; return updated; });
                                        }}
                                        errorText={optionErrors[idx] || ""}
                                    />
                                </div>
                                <div className="option-action-wrapper">
                                    {formData.options.length > 2 && (
                                        <Button
                                            danger
                                            type="text"
                                            icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                            onClick={() => removeOption(idx)}
                                            className="option-delete-btn"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Child Questions for Paragraph Type */}
                {formData.questionType === 6 && (
                    <div className="child-questions-section" style={{ marginTop: 24, borderTop: '1px solid #eee', paddingTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h4 style={{ margin: 0 }}>Child Questions</h4>
                            <Button type="primary" size="small" onClick={handleAddChildQuestion}>
                                + Add Sub-Question
                            </Button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {formData.childQuestions.map((child, cIdx) => (
                                <div key={cIdx} style={{ background: '#fafafa', padding: 16, borderRadius: 8, border: '1px solid #f0f0f0', position: 'relative' }}>
                                    <div style={{ position: 'absolute', right: 8, top: 8 }}>
                                        <Button
                                            danger
                                            type="text"
                                            icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                            onClick={() => handleRemoveChildQuestion(cIdx)}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                                        <div style={{ flex: 1 }}>
                                            <SelectInput
                                                title={`Sub-Question ${cIdx + 1} Type`}
                                                options={QUESTION_TYPES.filter(t => t.value !== 6)} // Prevent nested paragraphs
                                                value={child.questionType}
                                                onChange={(val) => handleChildQuestionChange(cIdx, "questionType", val)}
                                                required
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <InputField
                                                title="Sort Order"
                                                type="number"
                                                value={child.sortOrder}
                                                onChange={(e) => handleChildQuestionChange(cIdx, "sortOrder", e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <SelectInput
                                            title="Syllabus"
                                            options={syllabusOptions}
                                            value={child.syllabusId}
                                            onChange={(val) => handleChildQuestionChange(cIdx, "syllabusId", val)}
                                            placeholder="Select syllabus (Optional)"
                                        />
                                    </div>

                                    <TextAreaField
                                        title="Question Text"
                                        value={child.questionText}
                                        onChange={(e) => handleChildQuestionChange(cIdx, "questionText", e.target.value)}
                                        required
                                        rows={2}
                                    />

                                    {/* Child Options Logic */}
                                    {(child.questionType === 3 || child.questionType === 4) && (
                                        <div className="options-container" style={{ marginTop: 12 }}>
                                            <div className="options-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span>Options ({child.questionType === 3 ? "Single Choice" : "Multi Choice"})</span>
                                                    <Checkbox
                                                        checked={(child.defaultOptionSet || 0) === 1}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            handleChildQuestionChange(cIdx, "isDefaultOptions", checked);
                                                            handleChildQuestionChange(cIdx, "defaultOptionSet", checked ? 1 : 0);
                                                        }}
                                                    >
                                                        Use Default Options 1
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={(child.defaultOptionSet || 0) === 2}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            handleChildQuestionChange(cIdx, "isDefaultOptions", checked);
                                                            handleChildQuestionChange(cIdx, "defaultOptionSet", checked ? 2 : 0);
                                                        }}
                                                    >
                                                        Use Default Options 2
                                                    </Checkbox>
                                                    <Popover
                                                        trigger="hover"
                                                        placement="topLeft"
                                                        title={<span style={{ fontWeight: 600 }}>Default Option Sets</span>}
                                                        content={DefaultOptionsPopoverContent}
                                                    >
                                                        <InfoCircleOutlined style={{ color: '#faad14', cursor: 'pointer', fontSize: 15 }} />
                                                    </Popover>
                                                </div>
                                                {!child.isDefaultOptions && (
                                                    <Button type="dashed" size="small" onClick={() => handleAddChildOption(cIdx)}>+ Add Option</Button>
                                                )}
                                            </div>

                                            {!child.isDefaultOptions && child.options.map((opt, oIdx) => (
                                                <div key={oIdx} className="option-row">
                                                    <div className="option-check-wrapper">
                                                        <input
                                                            type={child.questionType === 3 ? "radio" : "checkbox"}
                                                            className="option-check"
                                                            checked={opt.isCorrect}
                                                            onChange={(e) => handleChildOptionChange(cIdx, oIdx, "isCorrect", e.target.checked)}
                                                            name={`childOptionRadio-${cIdx}`}
                                                            title="Mark as correct answer"
                                                        />
                                                    </div>
                                                    <div className="option-input-wrapper">
                                                        <InputField
                                                            placeholder={`Option ${oIdx + 1}`}
                                                            value={opt.optionText}
                                                            onChange={(e) => handleChildOptionChange(cIdx, oIdx, "optionText", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="option-action-wrapper">
                                                        {child.options.length > 2 && (
                                                            <Button
                                                                danger
                                                                type="text"
                                                                icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                                                onClick={() => handleRemoveChildOption(cIdx, oIdx)}
                                                                className="option-delete-btn"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Child Answer Key Logic for other types */}
                                    {child.questionType === 1 && (
                                        <InputField
                                            title="Answer Key"
                                            value={child.answerKeyText}
                                            onChange={(e) => handleChildQuestionChange(cIdx, "answerKeyText", e.target.value)}
                                            style={{ marginTop: 12 }}
                                        />
                                    )}
                                    {child.questionType === 2 && (
                                        <TextAreaField
                                            title="Answer Key"
                                            value={child.answerKeyText}
                                            onChange={(e) => handleChildQuestionChange(cIdx, "answerKeyText", e.target.value)}
                                            style={{ marginTop: 12 }}
                                        />
                                    )}
                                    {child.questionType === 5 && (
                                        <DateField
                                            title="Answer Key Date"
                                            value={child.answerKeyDate ? dayjs(child.answerKeyDate) : null}
                                            onChange={(date) => handleChildQuestionChange(cIdx, "answerKeyDate", date)}
                                            style={{ marginTop: 12 }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CustomModal>
        </div>
    );
};

export default Questions;
